import { motion } from 'framer-motion';
import React from 'react';
import { FiActivity, FiCpu, FiEye, FiMessageSquare, FiTrendingUp, FiZap } from 'react-icons/fi';
import { Link } from 'react-router-dom';

const AIServicesSection = () => {
  const aiCapabilities = [
    {
      icon: FiActivity,
      title: "Machine Learning",
      description: "Advanced ML models for predictive analytics and automation"
    },
    {
      icon: FiMessageSquare,
      title: "Conversational AI",
      description: "Intelligent chatbots and virtual assistants"
    },
    {
      icon: FiEye,
      title: "Computer Vision",
      description: "Image recognition and visual data processing"
    },
    {
      icon: FiTrendingUp,
      title: "Predictive Analytics",
      description: "Data-driven insights for strategic decision making"
    },
    {
      icon: FiCpu,
      title: "Neural Networks",
      description: "Deep learning solutions for complex problems"
    },
    {
      icon: FiZap,
      title: "Process Automation",
      description: "Intelligent automation for operational efficiency"
    }
  ];

  return (
    <section id="ai-services" className="py-20 bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-purple-500/20 rounded-full filter blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-500/20 rounded-full filter blur-3xl animate-pulse animation-delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-indigo-500/20 rounded-full filter blur-3xl animate-pulse animation-delay-2000"></div>
      </div>

      {/* Grid Pattern */}
      <div className="absolute inset-0 bg-grid-white/[0.02] bg-[size:50px_50px]"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            viewport={{ once: true }}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-purple-500/20 backdrop-blur-sm border border-purple-400/30 rounded-full text-purple-300 text-sm font-medium mb-6 font-accent tracking-wide"
          >
            <FiActivity className="animate-pulse" />
            <span>Future Technology</span>
          </motion.div>
          
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            viewport={{ once: true }}
            className="text-4xl md:text-6xl font-bold mb-6 font-display tracking-tight"
          >
            <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 bg-clip-text text-transparent">
              AI & Intelligent
            </span>
            <br />
            <span className="text-white">Agent Development</span>
          </motion.h2>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            viewport={{ once: true }}
            className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed font-body font-light tracking-wide"
          >
            Harness the power of artificial intelligence to transform your business operations. 
            Our cutting-edge AI solutions drive innovation, enhance efficiency, and unlock new possibilities.
          </motion.p>
        </motion.div>

        {/* AI Capabilities Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {aiCapabilities.map((capability, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50, rotateY: -15 }}
              whileInView={{ opacity: 1, y: 0, rotateY: 0 }}
              transition={{ delay: index * 0.1, duration: 0.6 }}
              viewport={{ once: true }}
              whileHover={{ 
                scale: 1.05, 
                rotateY: 5,
                transition: { duration: 0.3 }
              }}
              className="group perspective-1000"
            >
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 h-full hover:bg-white/10 transition-all duration-300">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-12 transition-all duration-300">
                  <capability.icon className="text-white" size={28} />
                </div>
                <h3 className="text-xl font-bold text-white mb-4 font-heading tracking-wide">{capability.title}</h3>
                <p className="text-gray-300 leading-relaxed font-body">{capability.description}</p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* AI Process Visualization */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-8 md:p-12 mb-16"
        >
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-white mb-4 font-display tracking-tight">AI Development Pipeline</h3>
            <p className="text-gray-300 text-lg font-body font-light tracking-wide">From concept to deployment, our systematic approach ensures AI success</p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { phase: "Data", icon: "📊", desc: "Collection & Preparation" },
              { phase: "Model", icon: "🧠", desc: "Training & Optimization" },
              { phase: "Test", icon: "🔬", desc: "Validation & Testing" },
              { phase: "Deploy", icon: "🚀", desc: "Production & Monitoring" }
            ].map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.2, duration: 0.5 }}
                viewport={{ once: true }}
                className="text-center relative"
              >
                {index < 3 && (
                  <motion.div
                    initial={{ scaleX: 0 }}
                    style={{left:130}}
                    whileInView={{ scaleX: 1 }}
                    transition={{ delay: index * 0.7 + 0.5, duration: 0.8 }}
                    viewport={{ once: true }}
                    className="hidden md:block absolute top-12 left-full w-full h-0.5 bg-gradient-to-r from-purple-400 to-pink-400 origin-left"
                  />
                )}
                <div className="relative z-10">
                  <motion.div
                    whileHover={{ scale: 1.1, rotate: 360 }}
                    transition={{ duration: 0.5 }}
                    className="w-24 h-24 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-3xl mx-auto mb-4 shadow-lg shadow-purple-500/25"
                  >
                    {step.icon}
                  </motion.div>
                  <h4 className="font-bold text-white mb-2 text-lg font-heading tracking-wide">{step.phase}</h4>
                  <p className="text-sm text-gray-300 font-body">{step.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mt-16"
        >
          <Link to="/contact">
          <motion.button
            whileHover={{ 
              scale: 1.05,
              boxShadow: "0 20px 40px rgba(168, 85, 247, 0.4)"
            }}
            whileTap={{ scale: 0.95 }}
            className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-10 py-4 rounded-full font-semibold text-lg shadow-xl hover:shadow-2xl transition-all duration-300 relative overflow-hidden group font-accent tracking-wide"
          >
            <span className="relative z-10">Explore AI Solutions</span>
            <div className="absolute inset-0 bg-gradient-to-r from-pink-500 to-purple-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          </motion.button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default AIServicesSection; 